/**
 * AIContextSidebar - Types, style helpers, and quick prompt generation
 */

import {
  AlertTriangle,
  TrendingUp,
  Lightbulb,
  Sparkles,
  FileText,
  MessageSquare,
  Target,
  Users,
  Briefcase,
} from 'lucide-react'

export interface AIContext {
  entityType: 'job' | 'quote' | 'inspection' | 'email' | 'file' | 'contact' | 'report' | null
  entityId: string | null
  entityName?: string
  hubId: string
  pagePath: string
  additionalContext?: Record<string, any>
}

export interface AISuggestion {
  id: string
  type: 'anomaly' | 'recommendation' | 'insight' | 'warning' | 'opportunity'
  title: string
  description: string
  confidence: number
  action?: { label: string; url?: string; handler?: () => void }
  metadata?: Record<string, any>
}

export interface AIRelatedItem {
  id: string
  type: 'job' | 'quote' | 'file' | 'email' | 'inspection'
  title: string
  subtitle?: string
  relevance: number
  url: string
}

export interface AIChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  context?: string
}

export interface AIContextSidebarProps {
  context: AIContext
  isOpen: boolean
  onClose: () => void
  apiClient?: {
    post: (url: string, data: any) => Promise<any>
    get: (url: string) => Promise<any>
  }
  onAction?: (action: string, data: any) => void
}

export const getSuggestionStyles = (type: AISuggestion['type']) => {
  switch (type) {
    case 'anomaly':
      return { icon: AlertTriangle, bgColor: 'bg-amber-50 dark:bg-amber-900/20', textColor: 'text-amber-600 dark:text-amber-400', borderColor: 'border-amber-200 dark:border-amber-800' }
    case 'warning':
      return { icon: AlertTriangle, bgColor: 'bg-red-50 dark:bg-red-900/20', textColor: 'text-red-600 dark:text-red-400', borderColor: 'border-red-200 dark:border-red-800' }
    case 'opportunity':
      return { icon: TrendingUp, bgColor: 'bg-emerald-50 dark:bg-emerald-900/20', textColor: 'text-emerald-600 dark:text-emerald-400', borderColor: 'border-emerald-200 dark:border-emerald-800' }
    case 'recommendation':
      return { icon: Lightbulb, bgColor: 'bg-blue-50 dark:bg-blue-900/20', textColor: 'text-blue-600 dark:text-blue-400', borderColor: 'border-blue-200 dark:border-blue-800' }
    default:
      return { icon: Sparkles, bgColor: 'bg-purple-50 dark:bg-purple-900/20', textColor: 'text-purple-600 dark:text-purple-400', borderColor: 'border-purple-200 dark:border-purple-800' }
  }
}

export const getEntityIcon = (type: string) => {
  switch (type) {
    case 'job': return Briefcase
    case 'quote': case 'file': return FileText
    case 'email': return MessageSquare
    case 'inspection': return Target
    case 'contact': return Users
    default: return FileText
  }
}

export const getQuickPrompts = (context: AIContext): string[] => {
  const base = ['What should I know about this?', 'Any risks I should be aware of?', 'What are the next steps?']
  switch (context.entityType) {
    case 'job': return ['What is the current job health status?', 'Are there any budget concerns?', 'What QA inspections are due?', 'Show me related quotes and invoices', ...base]
    case 'quote': return ['Is this quote competitively priced?', 'Any missing line items?', 'What is the margin analysis?', 'Similar past quotes?', ...base]
    case 'inspection': return ['What items need attention?', 'Any recurring issues?', 'Generate QA summary', ...base]
    case 'email': return ['Summarize this email thread', 'What action items are mentioned?', 'Related job or quote?', ...base]
    default: return base
  }
}
